<template>
    <base-modal
        class="business-order-view-modal business-order-view-modal"
        :title="$t('businessOrders.details.title')"
        :config="{ focusFirst: false }"
    >
        <spinner :active="isActive" />

        <template slot="titleTooltip">
            <button-tooltip v-tooltip.bottom="{
                content: $t('businessOrders.create.tooltip'),
                delay: { show: 500, hide: 300 },
            }"
            />
        </template>

        <div class="v-spacing-sm">
            <flex-row column-spacing="lg"
                      class="flex--no-wrap"
            >
                <div class="col col--wrap-text">
                    <value-wrapper :label="$t('businessOrders.labels.id')">
                        {{ businessOrder.id | valueOrDash }}
                    </value-wrapper>
                </div>
                <div class="col col--wrap-text">
                    <value-wrapper :label="$t('businessOrders.labels.referenceNo')">
                        {{ businessOrder.referenceNumber | valueOrDash }}
                    </value-wrapper>
                </div>
                <div class="col">
                    <value-wrapper :label="$t('businessOrders.labels.warehouseId')">
                        {{ businessOrder.warehouseName | valueOrDash }}
                        <template
                            v-if="businessOrder.isWarehouseManuallySelected"
                            slot="labelTooltip"
                        >
                            <button-tooltip
                                v-tooltip.bottom="{
                                    content: $t('businessOrders.warehouse.detailsTooltip'),
                                    delay: { show: 500, hide: 300 },
                                }"
                                display-m
                            />
                        </template>
                    </value-wrapper>
                </div>
                <div class="col">
                    <value-wrapper :label="$t('businessOrders.labels.orderType')">
                        {{ $options.BusinessOrderTypeLabels[businessOrder.orderType] | valueOrDash }}
                    </value-wrapper>
                </div>
            </flex-row>
            <flex-row column-spacing="lg"
                      class="flex--no-wrap"
            >
                <div class="col">
                    <value-wrapper :label="$t('businessOrders.labels.status')">
                        {{ $options.BusinessOrderStatusLabels[businessOrder.status] | valueOrDash }}
                    </value-wrapper>
                </div>
                <div class="col">
                    <value-wrapper :label="$t('businessOrders.labels.shipDate')">
                        {{ businessOrder.shipDate | date('short', null) | valueOrDash }}
                    </value-wrapper>
                </div>
                <div class="col">
                    <value-wrapper :label="$t('businessOrders.labels.carrier')">
                        {{ businessOrder.carrier | valueOrDash }}
                    </value-wrapper>
                </div>
            </flex-row>
            <flex-row column-spacing="lg"
                      class="flex--no-wrap"
            >
                <div class="col">
                    <value-wrapper :label="$t('businessOrders.labels.carrierProNumber')">
                        {{ businessOrder.carrierProNumber | valueOrDash }}
                    </value-wrapper>
                </div>
            </flex-row>
        </div>
        <section v-if="businessOrder.internalHoldReason">
            <h2 class="collapsible-title heading-with-separator">
                <span class="heading-with-separator__inner">
                    {{ $t('businessOrders.labels.holdTitle') }}
                </span>
            </h2>
            <flex-row v-if="businessOrder.internalHoldReason"
                      column-spacing="lg"
                      class="flex--no-wrap"
            >
                <div class="col">
                    <value-wrapper :label="$t('businessOrders.labels.holdReasonSN')">
                        {{ businessOrder.internalHoldReason | valueOrDash }}
                    </value-wrapper>
                </div>
            </flex-row>
            <flex-row v-if="businessOrder.internalHoldComment"
                      column-spacing="lg"
                      class="flex--no-wrap"
            >
                <div class="col">
                    <value-wrapper :label="$t('businessOrders.labels.holdCommentsSN')">
                        {{ businessOrder.internalHoldComment | valueOrDash }}
                    </value-wrapper>
                </div>
            </flex-row>
        </section>
        <component :is="isTabletSize ? 'div' : 'base-scroll-container'"
                   is-in-modal
        >
            <consignee-information :business-order="businessOrder" />
            <order-information :business-order="businessOrder" />
            <handling-units :business-order="businessOrder"
                            :is-visible="canDocumentsBeEdited || canDocumentsBePreviewed"
                            :class="{ 'business-order-view-modal__handling-units' : noDocuments }"
            />
            <form-wrapper id="upload-form"
                          ref="form"
                          v-slot="{ invalid, loading }"
                          :in-modal="true"
                          :scroll-on-error="true"
            >
                <document-upload-section :dropzone-options="dropzoneOptions"
                                         :file-upload-title="shippingLabelUploadTitle"
                                         :file-upload-description="$t('businessOrders.labels.shippingLabelDescription')"
                                         :is-edit="canDocumentsBeEdited"
                                         :is-preview="canDocumentsBePreviewed"
                                         :shipping-documents="shippingLabels"
                                         :file-type="$options.OrderDocumentTypes.ShippingLabel"
                                         :order-id="businessOrder.id"
                                         @error="handleUploadError"
                                         @duplicate="handleDuplicateDocument"
                                         @max-file-length="handleMaxFileLength"
                                         @max-files-reached="handleMaxFilesReached"
                                         @invalid-file-name="handleInvalidFileName"
                />
                <document-upload-section :dropzone-options="dropzoneOptionsBillOfLanding"
                                         :file-upload-title="billOfLandingUploadTitle"
                                         :file-upload-description="$t('businessOrders.labels.billOfLandingDescription')"
                                         :is-edit="canDocumentsBeEdited"
                                         :is-preview="canDocumentsBePreviewed"
                                         :shipping-documents="billOfLandingDocuments"
                                         :file-type="$options.OrderDocumentTypes.BillOfLading"
                                         :order-id="businessOrder.id"
                                         @error="handleUploadError"
                                         @duplicate="handleDuplicateDocument"
                                         @max-file-length="handleMaxFileLength"
                                         @max-files-reached="handleMaxFilesReached"
                                         @invalid-file-name="handleInvalidFileName"
                />
                <document-upload-section :dropzone-options="dropzoneOptions"
                                         :file-upload-title="miscellaneousShippingDocumentUploadTitle"
                                         :file-upload-description="
                                             $t('businessOrders.labels.shippingDocumentDescription')
                                         "
                                         :is-edit="canDocumentsBeEdited"
                                         :is-preview="canDocumentsBePreviewed"
                                         :shipping-documents="miscellaneousShippingDocuments"
                                         :file-type="$options.OrderDocumentTypes.ShippingDocument"
                                         :order-id="businessOrder.id"
                                         @error="handleUploadError"
                                         @duplicate="handleDuplicateDocument"
                                         @max-file-length="handleMaxFileLength"
                                         @max-files-reached="handleMaxFilesReached"
                                         @invalid-file-name="handleInvalidFileName"
                />
            </form-wrapper>
        </component>
        <div class="modal__actions modal__actions--with-scroll">
            <base-button id="closeOrderDetailsBtn"
                         for-modal
                         @click="$emit('close')"
            >
                {{ $t('general.close') }}
            </base-button>
        </div>
    </base-modal>
</template>
<script>
import '@/assets/icons/exclamation-small.svg';
import '@/assets/icons/plus-round.svg';
import { mapGetters } from 'vuex';
import { $t, $tc } from '@/translations';
import { OrderStatuses, OrderDocumentTypes } from '@/core/enums';
import DropzoneMessages from '@/core/enums/dropzone-messages';
import Spinner from '@/components/Spinner';
import { ValueWrapper, FormWrapper, FormInput } from '@/components/form';
import BaseButton from '@/components/base/BaseButton';
import BaseScrollContainer from '@/components/base/BaseScrollContainer';
import BaseModal from '@/components/modal/BaseModal';
import CollapsibleBlock from '@/components/layout/CollapsibleBlock';
import ButtonTooltip from '@/components/buttons/ButtonTooltip';
import DropZone from '@/components/drop-zone/DropZone';
import BusinessOrdersFile from './BusinessOrdersFile';
import DocumentUploadSection from './DocumentUploadSection';
import ConsigneeInformation from './ConsigneeInformation';
import OrderInformation from './OrderInformation';
import HandlingUnits from './HandlingUnits';
import {
    BusinessOrderStatusLabels,
    BusinessOrderTypeLabels,
} from './BusinessOrderStatuses';

export default {
    name: 'BusinessOrderViewModal',
    components: {
        HandlingUnits,
        OrderInformation,
        ConsigneeInformation,
        DocumentUploadSection,
        BusinessOrdersFile,
        DropZone,
        BaseScrollContainer,
        BaseModal,
        BaseButton,
        ValueWrapper,
        FormWrapper,
        FormInput,
        CollapsibleBlock,
        ButtonTooltip,
        Spinner,
    },
    props: {
        businessOrder: {
            type: Object,
            required: true,
        },
    },
    data() {
        const dropzoneOptions = {
            url: `${process.env.VUE_APP_API_URL}/businessOrders/${this.businessOrder.id}/documents/by-type/`,
            maxFiles: 20,
            withCredentials: true,
            acceptedFiles: '.pdf',
            thumbnailWidth: 150,
            maxFilesize: 2.0,
        };

        return {
            dropzoneOptions: {
                ...dropzoneOptions,
            },
            dropzoneOptionsBillOfLanding: {
                ...dropzoneOptions,
                maxFiles: 1,
            },
        };
    },
    computed: {
        ...mapGetters('spinner', ['isActive']),
        isTabletSize() {
            return true; // disabled scroll inside modal dialog
        },
        canDocumentsBeEdited() {
            const editableStatuses = [
                OrderStatuses.InFulfillment,
                OrderStatuses.PackingComplete,
                OrderStatuses.ReadyForPickup,
                OrderStatuses.Accepted,
            ];

            return editableStatuses.includes(this.businessOrder.status) && this.$hasAccess('businessorders:write');
        },
        canDocumentsBePreviewed() {
            return (!this.canDocumentsBeEdited && this.businessOrder.status === OrderStatuses.Shipped) || !this.$hasAccess('businessorders:write');
        },
        shippingLabels() {
            return this.businessOrder.orderDocuments
                .filter(file => file.documentType === OrderDocumentTypes.ShippingLabel);
        },
        billOfLandingDocuments() {
            return this.businessOrder.orderDocuments
                .filter(file => file.documentType === OrderDocumentTypes.BillOfLading);
        },
        miscellaneousShippingDocuments() {
            return this.businessOrder.orderDocuments
                .filter(file => file.documentType === OrderDocumentTypes.ShippingDocument);
        },
        noDocuments() {
            return this.canDocumentsBePreviewed
                && this.shippingLabels.length === 0
                && this.billOfLandingDocuments.length === 0
                && this.miscellaneousShippingDocuments.length === 0;
        },
        shippingLabelUploadTitle() {
            return this.shippingLabels.length > 0 ? $t('businessOrders.labels.shippingLabel') : $t('businessOrders.labels.uploadShippingLabel');
        },
        billOfLandingUploadTitle() {
            return this.billOfLandingDocuments.length > 0 ? $t('businessOrders.labels.billOfLanding') : $t('businessOrders.labels.uploadBillOfLanding');
        },
        miscellaneousShippingDocumentUploadTitle() {
            return this.miscellaneousShippingDocuments.length > 0 ? $t('businessOrders.labels.shippingDocument') : $t('businessOrders.labels.uploadShippingDocument');
        },
    },
    methods: {
        handleUploadError(file, message) {
            let messageForToast;

            switch (true) {
            case DropzoneMessages.IncorrectFileType.test(message): messageForToast = $t('businessOrders.toasts.incorrectFileType', { type: file.name.substring(file.name.lastIndexOf('.') + 1).toUpperCase() });
                break;
            case DropzoneMessages.FileTooBig.test(message): messageForToast = $t('businessOrders.toasts.fileTooBig', { maxSize: this.dropzoneOptions.maxFilesize });
                break;
            default: return;
            }

            this.$toast.error(messageForToast);
        },
        handleDuplicateDocument() {
            this.$toast.error($t('businessOrders.toasts.duplicateFile'));
        },
        handleMaxFilesReached(fileAmount) {
            this.$toast.error($tc('businessOrders.toasts.maxFilesReached', fileAmount));
        },
        handleMaxFileLength(maxFileNameLength) {
            this.$toast.error($t('businessOrders.toasts.maxFileLengthReached', { nameLength: maxFileNameLength }));
        },
        handleInvalidFileName() {
            this.$toast.error($t('businessOrders.toasts.invalidFileName'));
        },
    },
    OrderDocumentTypes,
    BusinessOrderTypeLabels,
    BusinessOrderStatusLabels,
};
</script>

<style lang="scss" scoped>
.business-order-view-modal {
    &__handling-units {
        margin-bottom: 0;
        padding-bottom: 0;
    }
}

.flex--spacing-lg .col {
    margin-right: 10px;
    margin-left: 10px;
}
</style>
